import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import * as d3 from 'd3';

interface SpendByCategoryChartProps {
  data: any[];
}

interface HierarchyNode {
  name: string;
  value?: number;
  children?: HierarchyNode[];
}

const SpendByCategoryChart: React.FC<SpendByCategoryChartProps> = ({ data }) => {
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const [hierarchyData, setHierarchyData] = useState<HierarchyNode | null>(null);
  const [totalSpend, setTotalSpend] = useState<number>(0);

  useEffect(() => {
    if (!data || data.length === 0) {
      console.log('No data provided to SpendByCategoryChart');
      return;
    }

    console.log('Building hierarchy data for category chart');
    
    // Group data by category and subcategory
    const categoryMap: Record<string, Record<string, number>> = {};
    let total = 0;

    data.forEach((item) => {
      if (item.category && item.subcategory && item.amount) {
        const amount = typeof item.amount === 'number' ? item.amount : parseFloat(item.amount);
        if (!isNaN(amount)) {
          if (!categoryMap[item.category]) {
            categoryMap[item.category] = {};
          }
          
          categoryMap[item.category][item.subcategory] = 
            (categoryMap[item.category][item.subcategory] || 0) + amount;
          
          total += amount;
        }
      }
    });

    console.log('Category map created:', categoryMap);
    console.log('Total spend calculated:', total);

    // Convert to hierarchy data structure
    const hierarchyData: HierarchyNode = {
      name: "All Categories",
      children: Object.entries(categoryMap).map(([category, subcategories]) => ({
        name: category,
        children: Object.entries(subcategories).map(([subcategory, amount]) => ({
          name: subcategory,
          value: amount
        }))
      }))
    };

    console.log('Hierarchy data structure created:', hierarchyData);
    setHierarchyData(hierarchyData);
    setTotalSpend(total);
  }, [data]);

  useEffect(() => {
    if (!hierarchyData || !svgRef.current) {
      return;
    }

    console.log('Rendering SpendByCategoryChart with data');
    
    // Clear previous chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Set dimensions
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;
    
    // Create root hierarchy
    const root = d3.hierarchy<HierarchyNode>(hierarchyData)
      .sum(d => d.value || 0);
    
    // Create partition layout
    const partition = d3.partition<HierarchyNode>();
    
    // Apply partition layout to hierarchy
    const nodes = partition
      .size([width, height])
      .padding(1)(root);
    
    // Color scale
    const colorScale = d3.scaleOrdinal<string>()
      .domain(root.children?.map(d => d.data.name) || [])
      .range([
        theme.palette.primary.main,
        theme.palette.secondary.main,
        '#e15759',
        '#76b7b2',
        '#59a14f',
        '#edc949',
        '#af7aa1',
        '#9c755f',
      ]);
    
    // Create SVG
    const svg = d3.select(svgRef.current);
    
    // Create tooltip
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("background-color", "white")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("box-shadow", "0 2px 5px rgba(0,0,0,0.2)")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("z-index", 1000);
    
    // Draw rectangles for each node
    svg.selectAll("rect")
      .data(nodes.descendants())
      .enter()
      .append("rect")
      .attr("x", d => d.x0)
      .attr("y", d => d.y0)
      .attr("width", d => d.x1 - d.x0)
      .attr("height", d => d.y1 - d.y0)
      .attr("fill", d => {
        if (d.depth === 0) return "none";
        if (d.depth === 1) return colorScale(d.data.name);
        
        // Handle parent color with proper type checking
        if (d.parent) {
          const parentColor = colorScale(d.parent.data.name);
          const brighterColor = d3.color(parentColor)?.brighter(0.5);
          return brighterColor ? brighterColor.toString() : parentColor;
        }
        
        return "#cccccc"; // Fallback color
      })
      .attr("stroke", d => d.depth > 0 ? "#fff" : "none")
      .attr("stroke-width", 1)
      .style("cursor", "pointer")
      .on("mouseover", (event, d) => {
        tooltip.transition()
          .duration(200)
          .style("opacity", 0.9);
        
        const value = d.value || 0;
        const percentage = ((value / totalSpend) * 100).toFixed(1);
        
        tooltip.html(`
          <strong>${d.data.name}</strong><br/>
          $${value.toLocaleString()}<br/>
          ${percentage}% of total
        `)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", () => {
        tooltip.transition()
          .duration(500)
          .style("opacity", 0);
      });
    
    // Add text labels
    svg.selectAll("text")
      .data(nodes.descendants().filter(d => (d.x1 - d.x0) > 40 && (d.y1 - d.y0) > 20))
      .enter()
      .append("text")
      .attr("x", d => d.x0 + 5)
      .attr("y", d => d.y0 + 15)
      .text(d => d.data.name)
      .attr("font-size", "10px")
      .attr("fill", d => d.depth === 0 ? "none" : "#fff")
      .style("pointer-events", "none")
      .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.5)")
      .style("overflow", "hidden")
      .style("text-overflow", "ellipsis");
    
    // Clean up tooltip on unmount
    return () => {
      d3.select(".d3-tooltip").remove();
    };
  }, [hierarchyData, totalSpend, theme]);

  return (
    <Box sx={{ height: 300, position: 'relative', width: '100%' }}>
      {data && data.length > 0 && hierarchyData ? (
        <svg 
          ref={svgRef} 
          width="100%" 
          height="100%"
          style={{ overflow: 'visible' }}
        />
      ) : (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          border: '1px dashed #ccc',
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            No data available for visualisation
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SpendByCategoryChart; 